import { useState, useMemo, useEffect, useRef } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronRight, Check } from "lucide-react";
import type { Property, Expense, ExpenseCategorySettings } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { DEFAULT_HIGH_CONFIDENCE_SCORE } from "./_shared";

// Task #1908 — Mercury reconciliation suggestions banner + side panel.
// Renders nothing unless the host is opted into the `mercury_reconciliation`
// beta. Two-step fetch: a cheap `beta-status` ping first, then the gated
// candidates list. Both Merge and Dismiss invalidate the expenses list so
// the soft-deleted manual row drops out of the table.
type MergeCandidateRow = {
  id: number;
  status: "open" | "merged" | "dismissed" | "snoozed";
  matchScore: string;
  amountDiff: string;
  daysDiff: number;
  vendorOverlap: string;
  mergedAt: string | null;
  // Task #1952 — actor audit fields surfaced for the merged/unmerged captions.
  mergedByUserId?: number | null;
  mergedByName?: string | null;
  unmergedAt?: string | null;
  unmergedByUserId?: number | null;
  unmergedByName?: string | null;
  // Task #1974 — populated only when the scorer auto-merged the candidate;
  // manual UI merges leave both NULL. The UI uses `autoMergeBatchId` to
  // group "Recently merged" rows precisely (with timestamp clustering as a
  // fallback for legacy auto-merge rows that pre-date the stamping).
  autoMergeBatchId?: string | null;
  autoMergedAt?: string | null;
  mercuryExpense: Expense;
  manualExpense: Expense;
  // Task #1910 — extras populated for many-to-one (split-receipt) candidates.
  extraMercuryExpenses?: Expense[];
  extraManualExpenses?: Expense[];
  // Task #2313 — true when the corresponding expense has split children. The
  // bank debit amount matches the parent total, so the reconciliation UI
  // labels the row "Split" to explain why a single statement line lines up
  // with a row that is broken into multiple categorized children on the P&L.
  mercuryExpenseHasSplits?: boolean;
  manualExpenseHasSplits?: boolean;
  extraMercuryExpenseSplitIds?: number[];
  extraManualExpenseSplitIds?: number[];
};

// Task #1952 — formats a short "Merged by Jane on Mar 3" style caption.
function formatActorCaption(verb: string, name: string | null | undefined, when: string | null | undefined): string | null {
  if (!when) return null;
  const date = new Date(when);
  if (Number.isNaN(date.getTime())) return null;
  const dateLabel = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const who = name?.trim() ? name.trim() : "someone";
  return `${verb} by ${who} on ${dateLabel}`;
}

type BulkRecapItem = {
  id: number;
  ok: boolean;
  message?: string;
  candidate?: MergeCandidateRow;
  retrying?: boolean;
  retryStatus?: "success" | "error";
  retryMessage?: string;
};

type BulkRecap = {
  action: "merge" | "dismiss" | "snooze" | "unmerge";
  items: BulkRecapItem[];
};

export function MercuryMergeCandidatesPanel({ properties }: { properties: Property[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Task #1974 — deep-link target from the `mercury_auto_merged`
  // notification (`?autoMergeBatch=<id>`). When present, auto-open the
  // dialog and scroll/highlight the matching batch so the host lands
  // directly on the rows that just got auto-merged.
  const search = useSearch();
  const [, navigate] = useLocation();
  const highlightedAutoBatchId = new URLSearchParams(search).get("autoMergeBatch");
  const highlightedBatchRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (highlightedAutoBatchId) setOpen(true);
  }, [highlightedAutoBatchId]);
  useEffect(() => {
    if (!open || !highlightedAutoBatchId) return;
    const t = setTimeout(() => {
      highlightedBatchRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => clearTimeout(t);
  }, [open, highlightedAutoBatchId]);
  // Strip the param once the dialog closes so re-opening later doesn't
  // pop the user back to a stale batch.
  const clearDeepLink = () => {
    if (!highlightedAutoBatchId) return;
    const next = new URLSearchParams(search);
    next.delete("autoMergeBatch");
    const qs = next.toString();
    navigate(`/app/financials${qs ? `?${qs}` : ""}`, { replace: true });
  };
  // Task #1965 — separate selection state for the Recently merged panel so a
  // host can multi-select within an auto-merge batch and bulk-unmerge without
  // touching the open-candidate selection.
  const [selectedMergedIds, setSelectedMergedIds] = useState<Set<number>>(new Set());
  const [bulkRecap, setBulkRecap] = useState<BulkRecap | null>(null);
  const [recapShowSuccess, setRecapShowSuccess] = useState(false);

  const betaQuery = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/app/expenses/merge-candidates/beta-status"],
  });

  const settingsQuery = useQuery<ExpenseCategorySettings>({
    queryKey: ["/api/app/expense-category-settings"],
  });
  const highConfidenceScore =
    settingsQuery.data?.mercuryHighConfidenceThreshold ?? DEFAULT_HIGH_CONFIDENCE_SCORE;
  // Task #1965 — surfaced in the Recently merged panel so a regretful host
  // can disable auto-merge inline next to the batch they want to roll back.
  const autoMergeEnabled = settingsQuery.data?.mercuryAutoMergeEnabled ?? false;

  const enabled = betaQuery.data?.enabled === true;

  const [showHistorical, setShowHistorical] = useState(false);
  const [historicalSearch, setHistoricalSearch] = useState("");
  const candidatesQuery = useQuery<{
    open: MergeCandidateRow[];
    recentlyMerged: MergeCandidateRow[];
    historicalMerged: MergeCandidateRow[];
    historicalMergedCount: number;
  }>({
    queryKey: ["/api/app/expenses/merge-candidates", showHistorical ? "historical" : "default"],
    queryFn: async () => {
      const url = showHistorical
        ? "/api/app/expenses/merge-candidates?historical=1"
        : "/api/app/expenses/merge-candidates";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load merge candidates");
      return res.json();
    },
    enabled,
  });

  // Task #2308 — top-level sorted IDs for shift-click range-select on the
  // Recently Merged panel. Mirrors the sort order applied inside the render.
  const sortedMergedCandidateIds = useMemo(
    () =>
      [...(candidatesQuery.data?.recentlyMerged ?? [])].sort(
        (a, b) =>
          new Date(b.mergedAt ?? 0).getTime() - new Date(a.mergedAt ?? 0).getTime(),
      ).map((r) => r.id),
    [candidatesQuery.data?.recentlyMerged],
  );
  const { handleItemClick: handleMergedCandidateClick } = useRangeSelection(
    sortedMergedCandidateIds,
    selectedMergedIds,
    setSelectedMergedIds,
  );

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/expenses/merge-candidates"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
    setSelectedIds(new Set());
    setSelectedMergedIds(new Set());
  };

  const mergeMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/expenses/merge-candidates/${id}/merge`, {}),
    onSuccess: () => { refetchAll(); toast({ title: "Merged" }); },
    onError: (err) => toast({ title: "Merge failed", description: parseErrorMessage(err, "Couldn't merge these expenses. Please try again."), variant: "destructive" }),
  });
  const dismissMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/expenses/merge-candidates/${id}/dismiss`, {}),
    onSuccess: () => { refetchAll(); },
    onError: (err) => toast({ title: "Dismiss failed", description: parseErrorMessage(err, "Couldn't dismiss this suggestion. Please try again."), variant: "destructive" }),
  });
  const snoozeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/expenses/merge-candidates/${id}/snooze`, { days: 30 }),
    onSuccess: () => { refetchAll(); toast({ title: "Snoozed for 30 days" }); },
    onError: (err) => toast({ title: "Snooze failed", description: parseErrorMessage(err, "Couldn't snooze this suggestion. Please try again."), variant: "destructive" }),
  });
  const unmergeMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/expenses/merge-candidates/${id}/unmerge`, {}),
    onSuccess: () => { refetchAll(); toast({ title: "Unmerged" }); },
    onError: (err) => toast({ title: "Unmerge failed", description: parseErrorMessage(err, "Couldn't unmerge this expense. Please try again."), variant: "destructive" }),
  });
  // Task #1965 — bulk-unmerge an entire auto-merge batch in one click.
  // Task #1975 — surfaces a per-row recap dialog (with retry) instead of a
  // single toast so partial failures don't get lost.
  const bulkUnmergeMutation = useMutation<
    { action: string; successCount: number; failureCount: number; results: Array<{ id: number; ok: boolean; message?: string }> },
    Error,
    { ids: number[]; snapshot: Map<number, MergeCandidateRow> }
  >({
    mutationFn: async ({ ids }) => {
      const res = await apiRequest("POST", "/api/app/expenses/merge-candidates/bulk", { ids, action: "unmerge" });
      return res.json();
    },
    onSuccess: (data, variables) => {
      refetchAll();
      const items: BulkRecapItem[] = data.results.map((r) => ({
        id: r.id,
        ok: r.ok,
        message: r.message,
        candidate: variables.snapshot.get(r.id),
      }));
      setBulkRecap({ action: "unmerge", items });
      setRecapShowSuccess(false);
    },
    onError: (err) => toast({ title: "Bulk unmerge failed", description: parseErrorMessage(err, "Couldn't unmerge these expenses. Please try again."), variant: "destructive" }),
  });
  // Task #1965 — inline shortcut so a host who regrets opting in can flip
  // auto-merge off without leaving the Reconcile dialog.
  const disableAutoMergeMutation = useMutation({
    mutationFn: async () => apiRequest("PUT", "/api/app/expense-category-settings", { mercuryAutoMergeEnabled: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      toast({
        title: "Auto-merge turned off",
        description: "New Mercury matches will wait for you to merge them.",
      });
    },
    onError: (err) => toast({ title: "Could not disable auto-merge", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  type BulkAction = "merge" | "dismiss" | "snooze" | "unmerge";
  type BulkResult = { action: BulkAction; successCount: number; failureCount: number; results: Array<{ id: number; ok: boolean; message?: string }> };
  const bulkMutation = useMutation<
    BulkResult,
    Error,
    { ids: number[]; action: BulkAction; days?: number; snapshot: Map<number, MergeCandidateRow> }
  >({
    mutationFn: async (payload) => {
      const res = await apiRequest("POST", "/api/app/expenses/merge-candidates/bulk", {
        ids: payload.ids,
        action: payload.action,
        ...(payload.days !== undefined ? { days: payload.days } : {}),
      });
      return (await res.json()) as BulkResult;
    },
    onSuccess: (data, variables) => {
      refetchAll();
      const items: BulkRecapItem[] = data.results.map((r) => ({
        id: r.id,
        ok: r.ok,
        message: r.message,
        candidate: variables.snapshot.get(r.id),
      }));
      setBulkRecap({ action: data.action, items });
      setRecapShowSuccess(false);
    },
    onError: (err) => toast({ title: "Bulk action failed", description: parseErrorMessage(err, "Couldn't complete this action. Please try again."), variant: "destructive" }),
  });

  const retrySingle = async (item: BulkRecapItem, action: BulkAction) => {
    setBulkRecap((prev) => prev
      ? { ...prev, items: prev.items.map((it) => it.id === item.id ? { ...it, retrying: true, retryStatus: undefined, retryMessage: undefined } : it) }
      : prev);
    try {
      const path = action === "merge"
        ? `/api/app/expenses/merge-candidates/${item.id}/merge`
        : action === "dismiss"
          ? `/api/app/expenses/merge-candidates/${item.id}/dismiss`
          : action === "unmerge"
            ? `/api/app/expenses/merge-candidates/${item.id}/unmerge`
            : `/api/app/expenses/merge-candidates/${item.id}/snooze`;
      const body = action === "snooze" ? { days: 30 } : {};
      await apiRequest("POST", path, body);
      setBulkRecap((prev) => prev
        ? { ...prev, items: prev.items.map((it) => it.id === item.id ? { ...it, retrying: false, retryStatus: "success", ok: true, message: undefined } : it) }
        : prev);
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses/merge-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
    } catch (err) {
      setBulkRecap((prev) => prev
        ? { ...prev, items: prev.items.map((it) => it.id === item.id ? { ...it, retrying: false, retryStatus: "error", retryMessage: parseErrorMessage(err, "Couldn't complete this action. Please try again.") } : it) }
        : prev);
    }
  };

  // Task #1946 — confirm unmerge for historical (>30d) matches because
  // the host may have forgotten the original context.
  const confirmHistoricalUnmerge = (row: MergeCandidateRow) => {
    const when = row.mergedAt ? new Date(row.mergedAt) : null;
    const whenLabel = when
      ? when.toLocaleString("en-US", { month: "long", year: "numeric" })
      : "an unknown date";
    const amount = Number(row.mercuryExpense.amount).toLocaleString("en-US", { style: "currency", currency: "USD" });
    const vendor = row.mercuryExpense.vendor || "this transaction";
    const mergerLabel = row.mergedByName?.trim() ? ` by ${row.mergedByName.trim()}` : "";
    const ok = window.confirm(
      `Unmerge ${vendor} for ${amount}?\n\n` +
      `This match was merged${mergerLabel} in ${whenLabel}. Unmerging restores the manual entry and reverts the Mercury row's category/notes/vendor to their pre-merge values. The candidate will reappear in the open queue.`
    );
    if (ok) unmergeMutation.mutate(row.id);
  };

  if (!enabled) return null;
  const openCandidates = candidatesQuery.data?.open ?? [];
  const openCount = openCandidates.length;
  const mergedCount = candidatesQuery.data?.recentlyMerged.length ?? 0;
  const historicalCount = candidatesQuery.data?.historicalMergedCount ?? 0;
  if (openCount === 0 && mergedCount === 0 && historicalCount === 0) return null;

  const propName = (id: number | null) => id ? (properties.find((p) => p.id === id)?.name ?? "—") : "—";
  const fmtMoney = (v: string) => Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const fmtDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString();

  const highConfidenceIds = openCandidates
    .filter((c) => Number(c.matchScore) >= highConfidenceScore)
    .map((c) => c.id);
  const highConfidenceCount = highConfidenceIds.length;
  const allHighConfidenceSelected = highConfidenceCount > 0 && highConfidenceIds.every((id) => selectedIds.has(id));
  const toggleHighConfidence = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const id of highConfidenceIds) next.add(id);
      } else {
        for (const id of highConfidenceIds) next.delete(id);
      }
      return next;
    });
  };
  const toggleOne = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const selectedCount = selectedIds.size;
  const runBulk = (action: "merge" | "dismiss" | "snooze") => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const snapshot = new Map<number, MergeCandidateRow>();
    for (const c of openCandidates) {
      if (selectedIds.has(c.id)) snapshot.set(c.id, c);
    }
    bulkMutation.mutate(
      action === "snooze"
        ? { ids, action, days: 30, snapshot }
        : { ids, action, snapshot },
    );
  };

  const actionPastTense = (a: BulkAction) => a === "merge" ? "Merged" : a === "dismiss" ? "Dismissed" : a === "unmerge" ? "Unmerged" : "Snoozed";
  const actionVerb = (a: BulkAction) => a === "merge" ? "Merge" : a === "dismiss" ? "Dismiss" : a === "unmerge" ? "Unmerge" : "Snooze";
  const recapSuccessCount = bulkRecap?.items.filter((it) => it.ok).length ?? 0;
  const recapFailureCount = bulkRecap ? bulkRecap.items.length - recapSuccessCount : 0;
  const renderCandidateSummary = (c: MergeCandidateRow | undefined, id: number) => {
    if (!c) return <span className="text-gray-500">Candidate #{id}</span>;
    return (
      <span>
        {fmtDate(c.mercuryExpense.date)} · {fmtMoney(c.mercuryExpense.amount)} · {c.mercuryExpense.vendor || "—"}
      </span>
    );
  };

  return (
    <>
      {openCount > 0 && (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-4 py-3 text-sm"
          data-testid="merge-candidates-banner"
        >
          <div className="text-blue-900 dark:text-blue-200">
            <strong>{openCount}</strong> possible duplicate{openCount === 1 ? "" : "s"} between Mercury imports and manual entries.
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="button-review-merge-candidates">
            Review
          </Button>
        </div>
      )}
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) clearDeepLink();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="merge-candidates-dialog">
          <DialogHeader>
            <DialogTitle>Reconcile Mercury imports</DialogTitle>
          </DialogHeader>
          {candidatesQuery.isLoading ? (
            <div className="space-y-2"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
          ) : (
            <div className="space-y-4">
              {openCount === 0 && <p className="text-sm text-gray-500">No open suggestions.</p>}
              {openCount > 0 && (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-gray-50 px-3 py-2 text-sm"
                  data-testid="bulk-actions-toolbar"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={allHighConfidenceSelected}
                      onCheckedChange={(v) => toggleHighConfidence(v === true)}
                      disabled={highConfidenceCount === 0}
                      data-testid="checkbox-select-high-confidence"
                    />
                    <span>
                      Select all high-confidence
                      <span className="text-gray-500" data-testid="text-high-confidence-threshold"> ({highConfidenceCount} ≥ {highConfidenceScore})</span>
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500" data-testid="text-bulk-selected-count">
                      {selectedCount} selected
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => runBulk("snooze")}
                      disabled={selectedCount === 0 || bulkMutation.isPending}
                      data-testid="button-bulk-snooze"
                    >
                      Snooze 30d
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runBulk("dismiss")}
                      disabled={selectedCount === 0 || bulkMutation.isPending}
                      data-testid="button-bulk-dismiss"
                    >
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => runBulk("merge")}
                      disabled={selectedCount === 0 || bulkMutation.isPending}
                      data-testid="button-bulk-merge"
                    >
                      {bulkMutation.isPending ? "Working…" : "Merge"}
                    </Button>
                  </div>
                </div>
              )}
              {candidatesQuery.data?.open.map((c) => {
                const mercuryRows = [c.mercuryExpense, ...(c.extraMercuryExpenses ?? [])];
                const manualRows = [c.manualExpense, ...(c.extraManualExpenses ?? [])];
                const isSplit = mercuryRows.length > 1 || manualRows.length > 1;
                const splitLabel = mercuryRows.length > 1
                  ? `Split: ${mercuryRows.length} Mercury → 1 manual`
                  : manualRows.length > 1
                    ? `Split: 1 Mercury → ${manualRows.length} manual`
                    : null;
                return (
                <div key={c.id} className="rounded-md border p-3" data-testid={`merge-candidate-${c.id}`}>
                  <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedIds.has(c.id)}
                        onCheckedChange={(v) => toggleOne(c.id, v === true)}
                        data-testid={`checkbox-candidate-${c.id}`}
                      />
                      <span>Match score: {Math.round(Number(c.matchScore))}/100 · ±{fmtMoney(c.amountDiff)} · {c.daysDiff}d apart</span>
                    </label>
                    {splitLabel && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900" data-testid={`split-badge-${c.id}`}>
                        {splitLabel}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded bg-blue-50 dark:bg-blue-950/40 p-2 space-y-2">
                      <div className="font-medium text-blue-900 dark:text-blue-200">
                        Mercury{mercuryRows.length > 1 ? ` (${mercuryRows.length} rows, all kept)` : " (kept)"}
                      </div>
                      {mercuryRows.map((m, i) => {
                        // Task #2313 — flag split parents so the row total
                        // (which matches the bank debit) is clearly labeled.
                        const extraSplitIds = new Set(c.extraMercuryExpenseSplitIds ?? []);
                        const isSplitParent = i === 0
                          ? !!c.mercuryExpenseHasSplits
                          : extraSplitIds.has(m.id);
                        return (
                          <div key={m.id} data-testid={`merge-candidate-${c.id}-mercury-${i}`} className={i > 0 ? "border-t border-blue-100 pt-2" : ""}>
                            <div className="flex items-center gap-2">
                              <span>{fmtDate(m.date)} · {fmtMoney(m.amount)}</span>
                              {isSplitParent && (
                                <span
                                  className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800"
                                  title="This expense is split into multiple categorized children. The bank debit matches the parent total."
                                  data-testid={`split-parent-badge-mercury-${m.id}`}
                                >
                                  Split
                                </span>
                              )}
                            </div>
                            <div className="text-gray-600">{m.vendor || "—"}</div>
                            <div className="text-xs text-gray-500">{propName(m.propertyId)}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="rounded bg-gray-50 p-2 space-y-2">
                      <div className="font-medium">
                        Manual{manualRows.length > 1 ? ` (${manualRows.length} rows, all voided on merge)` : " (voided on merge)"}
                      </div>
                      {manualRows.map((x, i) => {
                        const extraSplitIds = new Set(c.extraManualExpenseSplitIds ?? []);
                        const isSplitParent = i === 0
                          ? !!c.manualExpenseHasSplits
                          : extraSplitIds.has(x.id);
                        return (
                          <div key={x.id} data-testid={`merge-candidate-${c.id}-manual-${i}`} className={i > 0 ? "border-t border-gray-200 pt-2" : ""}>
                            <div className="flex items-center gap-2">
                              <span>{fmtDate(x.date)} · {fmtMoney(x.amount)}</span>
                              {isSplitParent && (
                                <span
                                  className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800"
                                  title="This expense is split into multiple categorized children. The bank debit matches the parent total."
                                  data-testid={`split-parent-badge-manual-${x.id}`}
                                >
                                  Split
                                </span>
                              )}
                            </div>
                            <div className="text-gray-600">{x.vendor || "—"}</div>
                            <div className="text-xs text-gray-500">{propName(x.propertyId)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {isSplit && (
                    <div className="mt-2 text-xs text-gray-500">
                      Sums match within {fmtMoney(c.amountDiff)}. Merging will void all manual rows above and keep all Mercury rows intact.
                    </div>
                  )}
                  <div className="mt-2 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => snoozeMutation.mutate(c.id)} disabled={snoozeMutation.isPending} data-testid={`button-snooze-${c.id}`}>Snooze 30d</Button>
                    <Button size="sm" variant="outline" onClick={() => dismissMutation.mutate(c.id)} disabled={dismissMutation.isPending} data-testid={`button-dismiss-${c.id}`}>Not a duplicate</Button>
                    <Button size="sm" onClick={() => mergeMutation.mutate(c.id)} disabled={mergeMutation.isPending} data-testid={`button-merge-${c.id}`}>Merge</Button>
                  </div>
                </div>
                );
              })}
              {mergedCount > 0 && (() => {
                // Task #1974 — group Recently merged rows by the scorer's
                // explicit `autoMergeBatchId` when present so an auto-merge
                // run is always one batch (and a teammate's one-off Merge
                // click stays its own batch of 1) regardless of whether the
                // clicks happened to fall within 2 minutes of each other.
                // Manual merges have NULL batchIds and fall through to the
                // legacy 2-minute timestamp clustering for back-compat with
                // pre-Task-#1974 auto-merge rows.
                const BATCH_WINDOW_MS = 2 * 60 * 1000;
                const sorted = [...candidatesQuery.data!.recentlyMerged].sort(
                  (a, b) => (new Date(b.mergedAt ?? 0).getTime()) - (new Date(a.mergedAt ?? 0).getTime()),
                );
                type Batch = {
                  key: string;
                  rows: MergeCandidateRow[];
                  latest: number;
                  earliest: number;
                  autoMergeBatchId: string | null;
                };
                const batches: Batch[] = [];
                const batchByAutoId = new Map<string, Batch>();
                for (const row of sorted) {
                  const t = row.mergedAt ? new Date(row.mergedAt).getTime() : 0;
                  const autoId = row.autoMergeBatchId ?? null;
                  if (autoId) {
                    const existing = batchByAutoId.get(autoId);
                    if (existing) {
                      existing.rows.push(row);
                      existing.earliest = Math.min(existing.earliest, t);
                      existing.latest = Math.max(existing.latest, t);
                      continue;
                    }
                    const batch: Batch = {
                      key: `auto-${autoId}`,
                      rows: [row],
                      earliest: t,
                      latest: t,
                      autoMergeBatchId: autoId,
                    };
                    batchByAutoId.set(autoId, batch);
                    batches.push(batch);
                    continue;
                  }
                  const last = batches[batches.length - 1];
                  if (last && last.autoMergeBatchId == null && Math.abs(last.earliest - t) <= BATCH_WINDOW_MS) {
                    last.rows.push(row);
                    last.earliest = Math.min(last.earliest, t);
                    last.latest = Math.max(last.latest, t);
                  } else {
                    batches.push({ key: `batch-${row.id}`, rows: [row], earliest: t, latest: t, autoMergeBatchId: null });
                  }
                }
                const fmtBatchTime = (ms: number) => {
                  if (!ms) return "—";
                  const d = new Date(ms);
                  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
                };
                const toggleBatch = (batch: Batch, checked: boolean) => {
                  setSelectedMergedIds((prev) => {
                    const next = new Set(prev);
                    for (const r of batch.rows) {
                      if (checked) next.add(r.id);
                      else next.delete(r.id);
                    }
                    return next;
                  });
                };
                const toggleMergedOne = (id: number, checked: boolean) => {
                  setSelectedMergedIds((prev) => {
                    const next = new Set(prev);
                    if (checked) next.add(id);
                    else next.delete(id);
                    return next;
                  });
                };
                const runBulkUnmerge = (ids: number[]) => {
                  if (ids.length === 0) return;
                  const label = ids.length === 1 ? "this match" : `${ids.length} matches`;
                  if (!window.confirm(`Unmerge ${label}? This restores the manual entries and reverts the Mercury rows to their pre-merge state.`)) return;
                  const snapshot = new Map<number, MergeCandidateRow>();
                  for (const r of sorted) {
                    if (ids.includes(r.id)) snapshot.set(r.id, r);
                  }
                  bulkUnmergeMutation.mutate({ ids, snapshot });
                };
                return (
                  <div data-testid="recently-merged-panel">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <h4 className="text-sm font-medium">Recently merged</h4>
                      {autoMergeEnabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs text-gray-600 hover:text-gray-900"
                          onClick={() => {
                            if (window.confirm("Turn off auto-merge? New Mercury matches will wait for you to merge them.")) {
                              disableAutoMergeMutation.mutate();
                            }
                          }}
                          disabled={disableAutoMergeMutation.isPending}
                          data-testid="button-disable-auto-merge-header"
                        >
                          Disable auto-merge
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {batches.map((batch) => {
                        const batchIds = batch.rows.map((r) => r.id);
                        const allSelected = batchIds.every((id) => selectedMergedIds.has(id));
                        const someSelected = batchIds.some((id) => selectedMergedIds.has(id));
                        const selectedInBatch = batchIds.filter((id) => selectedMergedIds.has(id));
                        // Task #1974 — an explicit autoMergeBatchId is the
                        // ground-truth signal that the scorer auto-merged
                        // this run. Fall back to the legacy "size > 1"
                        // heuristic so pre-stamping rows still render as
                        // auto batches.
                        const isAutoMergeBatch = batch.autoMergeBatchId != null || batch.rows.length > 1;
                        const isHighlighted = batch.autoMergeBatchId != null && batch.autoMergeBatchId === highlightedAutoBatchId;
                        const countLabel = batch.rows.length === 1 ? "1 match" : `${batch.rows.length} matches`;
                        const batchLabel = batch.autoMergeBatchId != null
                          ? `Auto-merged · ${countLabel} · ${fmtBatchTime(batch.latest)}`
                          : batch.rows.length === 1
                            ? `Merged ${fmtBatchTime(batch.latest)}`
                            : `Batch of ${batch.rows.length} · merged ${fmtBatchTime(batch.latest)}`;
                        return (
                          <div
                            key={batch.key}
                            ref={isHighlighted ? highlightedBatchRef : undefined}
                            className={
                              isHighlighted
                                ? "rounded border-2 border-blue-400 ring-2 ring-blue-200 shadow-sm"
                                : batch.autoMergeBatchId != null
                                  ? "rounded border border-blue-200"
                                  : "rounded border border-gray-200"
                            }
                            data-testid={`merged-batch-${batch.key}`}
                          >
                            <div className={`flex items-center justify-between gap-2 border-b px-2 py-1.5 text-xs ${batch.autoMergeBatchId != null ? "bg-blue-50 dark:bg-blue-950/40" : "bg-gray-50 dark:bg-gray-900/40"}`}>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                                  onCheckedChange={(v) => toggleBatch(batch, v === true)}
                                  data-testid={`checkbox-merged-batch-${batch.key}`}
                                />
                                <span className="font-medium text-gray-700" data-testid={`text-merged-batch-label-${batch.key}`}>
                                  {batchLabel}
                                </span>
                                {batch.autoMergeBatchId != null && (
                                  <span
                                    className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800"
                                    data-testid={`badge-auto-merged-${batch.key}`}
                                  >
                                    Auto
                                  </span>
                                )}
                              </label>
                              <div className="flex items-center gap-2">
                                {isAutoMergeBatch && autoMergeEnabled && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs text-gray-600 hover:text-gray-900"
                                    onClick={() => {
                                      if (window.confirm("Turn off auto-merge? New Mercury matches will wait for you to merge them.")) {
                                        disableAutoMergeMutation.mutate();
                                      }
                                    }}
                                    disabled={disableAutoMergeMutation.isPending}
                                    data-testid={`button-disable-auto-merge-${batch.key}`}
                                  >
                                    Disable auto-merge
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7"
                                  onClick={() => runBulkUnmerge(selectedInBatch.length > 0 ? selectedInBatch : batchIds)}
                                  disabled={bulkUnmergeMutation.isPending}
                                  data-testid={`button-bulk-unmerge-batch-${batch.key}`}
                                >
                                  {bulkUnmergeMutation.isPending
                                    ? "Unmerging…"
                                    : selectedInBatch.length > 0
                                      ? `Unmerge selected (${selectedInBatch.length})`
                                      : `Unmerge all (${batch.rows.length})`}
                                </Button>
                              </div>
                            </div>
                            <div className="divide-y">
                              {batch.rows.map((c) => {
                                const mergedCaption = formatActorCaption("Merged", c.mergedByName, c.mergedAt);
                                const unmergedCaption = formatActorCaption("Previously unmerged", c.unmergedByName, c.unmergedAt);
                                return (
                                  <div
                                    key={c.id}
                                    className="flex items-center justify-between gap-2 px-2 py-2 text-sm"
                                    data-testid={`merged-candidate-${c.id}`}
                                  >
                                    <div className="flex min-w-0 items-start gap-2 cursor-pointer" onClick={(e) => { e.stopPropagation(); handleMergedCandidateClick(c.id, e.shiftKey); }}>
                                      <Checkbox
                                        className="mt-0.5"
                                        checked={selectedMergedIds.has(c.id)}
                                        onCheckedChange={() => {}}
                                        data-testid={`checkbox-merged-candidate-${c.id}`}
                                      />
                                      <div className="min-w-0">
                                        <div className="truncate">
                                          {fmtDate(c.mercuryExpense.date)} · {fmtMoney(c.mercuryExpense.amount)} · {c.mercuryExpense.vendor || "—"}
                                        </div>
                                        {mergedCaption && (
                                          <div className="text-xs text-gray-500" data-testid={`text-merged-by-${c.id}`}>
                                            {mergedCaption}
                                          </div>
                                        )}
                                        {unmergedCaption && (
                                          <div className="text-xs text-gray-500" data-testid={`text-unmerged-by-${c.id}`}>
                                            {unmergedCaption}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => unmergeMutation.mutate(c.id)}
                                      disabled={unmergeMutation.isPending || bulkUnmergeMutation.isPending}
                                      data-testid={`button-unmerge-${c.id}`}
                                    >
                                      Unmerge
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {/* Task #1946 — historical (>30d) unmerge entry point. */}
              <div className="border-t pt-3">
                {!showHistorical ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowHistorical(true)}
                    disabled={candidatesQuery.isFetching}
                    data-testid="button-show-historical-merges"
                  >
                    Unmerge an older match…
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-medium">Older merges</h4>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setShowHistorical(false); setHistoricalSearch(""); }}
                        data-testid="button-hide-historical-merges"
                      >
                        Hide
                      </Button>
                    </div>
                    <Input
                      placeholder="Search by vendor, amount, or month (e.g. 'Lowe', '150', 'April')"
                      value={historicalSearch}
                      onChange={(e) => setHistoricalSearch(e.target.value)}
                      data-testid="input-historical-search"
                    />
                    {candidatesQuery.isFetching ? (
                      <Skeleton className="h-16" />
                    ) : (() => {
                      const all = candidatesQuery.data?.historicalMerged ?? [];
                      const q = historicalSearch.trim().toLowerCase();
                      const filtered = q
                        ? all.filter((c) => {
                            const when = c.mergedAt ? new Date(c.mergedAt) : null;
                            const hay = [
                              c.mercuryExpense.vendor ?? "",
                              c.mercuryExpense.description ?? "",
                              c.mercuryExpense.notes ?? "",
                              c.manualExpense.vendor ?? "",
                              c.manualExpense.description ?? "",
                              c.manualExpense.notes ?? "",
                              c.mercuryExpense.amount,
                              c.mercuryExpense.date,
                              when ? when.toLocaleString("en-US", { month: "long", year: "numeric" }) : "",
                            ].join(" ").toLowerCase();
                            return hay.includes(q);
                          })
                        : all;
                      if (all.length === 0) {
                        return <p className="text-sm text-gray-500" data-testid="text-no-historical-merges">No older merges to undo.</p>;
                      }
                      if (filtered.length === 0) {
                        return <p className="text-sm text-gray-500">No matches for "{historicalSearch}".</p>;
                      }
                      const shown = filtered.slice(0, 50);
                      return (
                        <>
                          <div className="space-y-2 max-h-72 overflow-y-auto">
                            {shown.map((c) => {
                              const when = c.mergedAt ? new Date(c.mergedAt) : null;
                              const mergedByCaption = c.mergedByName?.trim()
                                ? ` by ${c.mergedByName.trim()}`
                                : "";
                              const unmergedCaption = formatActorCaption("Previously unmerged", c.unmergedByName, c.unmergedAt);
                              return (
                                <div
                                  key={c.id}
                                  className="flex items-center justify-between gap-2 rounded border p-2 text-sm"
                                  data-testid={`historical-merged-candidate-${c.id}`}
                                >
                                  <div className="min-w-0">
                                    <div className="truncate">
                                      {fmtDate(c.mercuryExpense.date)} · {fmtMoney(c.mercuryExpense.amount)} · {c.mercuryExpense.vendor || "—"}
                                    </div>
                                    <div className="text-xs text-gray-500" data-testid={`text-historical-merged-by-${c.id}`}>
                                      Merged {when ? when.toLocaleDateString() : "—"}{mergedByCaption} · {propName(c.mercuryExpense.propertyId)}
                                    </div>
                                    {unmergedCaption && (
                                      <div className="text-xs text-gray-500" data-testid={`text-historical-unmerged-by-${c.id}`}>
                                        {unmergedCaption}
                                      </div>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => confirmHistoricalUnmerge(c)}
                                    disabled={unmergeMutation.isPending}
                                    data-testid={`button-historical-unmerge-${c.id}`}
                                  >
                                    Unmerge
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                          {filtered.length > shown.length && (
                            <p className="text-xs text-gray-500">
                              Showing first {shown.length} of {filtered.length}. Narrow your search to find more.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={bulkRecap !== null}
        onOpenChange={(v) => { if (!v) setBulkRecap(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-recap-dialog">
          {bulkRecap && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="text-bulk-recap-title">
                  {actionPastTense(bulkRecap.action)} {recapSuccessCount} of {bulkRecap.items.length}
                  {recapFailureCount > 0 ? ` · ${recapFailureCount} failed` : ""}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {recapFailureCount > 0 && (
                  <div>
                    <div className="mb-2 text-sm font-medium text-red-900" data-testid="text-bulk-recap-failed-heading">
                      Failed ({recapFailureCount})
                    </div>
                    <div className="space-y-2">
                      {bulkRecap.items.filter((it) => !it.ok).map((it) => (
                        <div
                          key={it.id}
                          className="rounded border border-red-200 bg-red-50 p-2 text-sm"
                          data-testid={`bulk-recap-failed-${it.id}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-red-900 truncate">
                                {renderCandidateSummary(it.candidate, it.id)}
                              </div>
                              <div className="mt-0.5 text-xs text-red-800" data-testid={`bulk-recap-failed-message-${it.id}`}>
                                {it.message ?? "Unknown error"}
                              </div>
                              {it.retryStatus === "error" && (
                                <div className="mt-0.5 text-xs text-red-800">
                                  Retry failed: {it.retryMessage ?? "Unknown error"}
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retrySingle(it, bulkRecap.action)}
                              disabled={!!it.retrying}
                              data-testid={`button-bulk-recap-retry-${it.id}`}
                            >
                              {it.retrying ? "Retrying…" : `Retry ${actionVerb(bulkRecap.action)}`}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {recapSuccessCount > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setRecapShowSuccess((v) => !v)}
                      className="flex w-full items-center gap-2 rounded border bg-gray-50 px-3 py-2 text-left text-sm font-medium hover:bg-gray-100"
                      data-testid="button-bulk-recap-toggle-success"
                    >
                      {recapShowSuccess ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Successful ({recapSuccessCount})
                    </button>
                    {recapShowSuccess && (
                      <div className="mt-2 space-y-1" data-testid="bulk-recap-success-list">
                        {bulkRecap.items.filter((it) => it.ok).map((it) => (
                          <div
                            key={it.id}
                            className="flex items-center gap-2 rounded border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-2 py-1.5 text-sm text-green-900 dark:text-green-200"
                            data-testid={`bulk-recap-success-${it.id}`}
                          >
                            <Check className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{renderCandidateSummary(it.candidate, it.id)}</span>
                            {it.retryStatus === "success" && (
                              <span className="ml-auto text-xs text-green-700">Retried</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setBulkRecap(null)}
                  data-testid="button-bulk-recap-close"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
